<script>
import AppButton from '@/components/ui/AppButton.vue'
import Icons from '@/components/Icons.vue'
import ProductRegisterFlow from '@/components/product/ProductRegisterFlow.vue'
import { getCategoryIcon, getSubCategoryIcon } from '@/utils/productIcons'

export default {
	name: 'ProfileProducts',
	components: {
		AppButton,
		Icons,
		ProductRegisterFlow
	},
	emits: ['delete', 'dirty-change', 'load-categories', 'load-sub-categories', 'save'],
	props: {
		products: {
			type: Array,
			default: () => []
		},
		categories: {
			type: Array,
			default: () => []
		},
		subCategories: {
			type: Array,
			default: () => []
		},
		isSaving: {
			type: Boolean,
			default: false
		},
		deletingProductIds: {
			type: Array,
			default: () => []
		},
		resetKey: {
			type: Number,
			default: 0
		},
		successMessage: {
			type: String,
			default: ''
		},
		requestError: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			activeTab: 'registered'
		}
	},
	watch: {
		resetKey() {
			this.$refs.flow?.reset()
			this.activeTab = 'registered'
		}
	},
	methods: {
		selectTab(tab) {
			this.activeTab = tab
			if (tab === 'register' && !this.categories.length)
				this.$emit('load-categories')
		},
		discardChanges() {
			this.$refs.flow?.reset()
			this.activeTab = 'registered'
		},
		getProductData(memberProduct) {
			return memberProduct?.product || {}
		},
		getCategory(memberProduct) {
			return this.getProductData(memberProduct)?.subCategory?.category || {}
		},
		getSubCategory(memberProduct) {
			return this.getProductData(memberProduct)?.subCategory || {}
		},
		cardIcon(memberProduct) {
			const categoryName = this.getCategory(memberProduct).name
			const subName = this.getSubCategory(memberProduct).name
			return getSubCategoryIcon(categoryName, subName) || getCategoryIcon(categoryName)
		}
	}
}
</script>

<template>
	<section class="profile-products profile__section-card" data-qa="profile-products-section" :aria-label="$t('myArea.menu.products')">
		<nav class="profile-products__tabs" data-qa="profile-products-tabs" :aria-label="$t('myArea.productsSection.tabsAriaLabel')">
			<AppButton
				data-qa="profile-products-registered-tab-item"
				variant="product-tab"
				:active="activeTab === 'registered'"
				@click="selectTab('registered')"
			>
				{{ $t('myArea.productsSection.registeredTab') }}
			</AppButton>
			<AppButton
				data-qa="profile-products-register-tab-item"
				variant="product-tab"
				:active="activeTab === 'register'"
				@click="selectTab('register')"
			>
				{{ $t('myArea.productsSection.registerTab') }}
			</AppButton>
		</nav>
		<Transition name="profile-feedback">
			<p v-if="successMessage" class="profile-products__feedback profile-products__feedback--success" data-qa="profile-product-success" role="status">
				<Icons name="check" aria-hidden="true" />
				{{ successMessage }}
			</p>
		</Transition>
		<p v-if="!successMessage && requestError" class="profile-products__feedback profile-products__feedback--error" data-qa="profile-product-error" role="alert">
			<Icons name="exclamation" aria-hidden="true" />
			{{ requestError }}
		</p>

		<div v-show="activeTab === 'registered'" class="profile-products__registered">
			<header class="profile-products__intro">
				<h2 class="profile-products__intro-title">{{ $t('myArea.productsSection.registeredTitle') }}</h2>
				<p class="profile-products__intro-text">{{ $t('myArea.productsSection.registeredIntro') }}</p>
			</header>

			<p v-if="!products.length" class="profile-products__empty" data-qa="profile-products-empty">
				{{ $t('myArea.textMSGProd') }}
			</p>

			<div v-else class="profile-products__grid">
				<article
					v-for="memberProduct in products"
					:key="memberProduct.id || getProductData(memberProduct).id"
					class="profile-products__card"
					data-qa="profile-products-item-card"
					:data-qa-id="memberProduct.id || getProductData(memberProduct).id"
				>
					<div class="profile-products__image" aria-hidden="true">
						<img
							v-if="cardIcon(memberProduct)"
							:src="cardIcon(memberProduct)"
							alt=""
						>
					</div>

					<div class="profile-products__content">
						<span class="profile-products__category" data-qa="profile-products-category-text" :data-qa-id="memberProduct.id || getProductData(memberProduct).id">{{ getCategory(memberProduct).name }}</span>
						<h2 data-qa="profile-products-name-title" :data-qa-id="memberProduct.id || getProductData(memberProduct).id">{{ getProductData(memberProduct).name }}</h2>
						<p data-qa="profile-products-subcategory-text" :data-qa-id="memberProduct.id || getProductData(memberProduct).id">{{ getSubCategory(memberProduct).name }}</p>
					</div>

					<AppButton
						class="profile-products__delete"
						data-qa="profile-products-delete-button"
						:data-qa-id="memberProduct.id || getProductData(memberProduct).id"
						variant="product-delete"
						icon="close"
						:disabled="deletingProductIds.includes(getProductData(memberProduct).id)"
						:aria-label="$t('myArea.productsSection.deleteAriaLabel', { product: getProductData(memberProduct).name })"
						:title="$t('myArea.productsSection.deleteAriaLabel', { product: getProductData(memberProduct).name })"
						@click="$emit('delete', getProductData(memberProduct).id)"
					/>
				</article>
			</div>
		</div>

		<ProductRegisterFlow
			v-show="activeTab === 'register'"
			ref="flow"
			:categories="categories"
			:sub-categories="subCategories"
			:is-saving="isSaving"
			@load-sub-categories="$emit('load-sub-categories', $event)"
			@save="$emit('save', $event)"
			@dirty-change="$emit('dirty-change', $event)"
		/>
	</section>
</template>

<style lang="sass" scoped>
@use '../../assets/sass/utilities/_responsive' as *

.profile-products
	display: flex
	flex-direction: column
	gap: 1.5rem
	width: 100%
	min-width: 0
	box-shadow: 0 0.25rem 0.25rem rgba(var(--lg-dark-gray-300-rgb), 0.06), 0 1.25rem 1.25rem rgba(var(--lg-dark-gray-300-rgb), 0.06), inset -0.03125rem -0.09375rem 0 rgba(var(--gray-30-rgb), 0.2)

	&__tabs
		display: flex
		align-items: center
		gap: 1rem
		overflow-x: auto
		scrollbar-width: thin

	&__registered
		min-width: 0
		display: flex
		flex-direction: column
		gap: 1.25rem

	&__intro
		display: flex
		flex-direction: column
		gap: 0.375rem

	&__intro-title
		margin: 0
		font-family: var(--lgei-headline)
		font-size: 1.375rem
		font-weight: 400
		color: var(--lg-dark-gray-200)

	&__intro-text
		margin: 0
		max-width: 60ch
		font-family: var(--lgei-text)
		font-size: 0.9375rem
		line-height: 1.5
		color: var(--lg-mid-gray-200)

	&__feedback
		display: flex
		align-items: center
		gap: 0.5rem
		width: fit-content
		min-height: 2.5rem
		margin: 0
		padding: 0.5rem 1rem
		border-radius: 0.5rem
		font-family: var(--lgei-text)
		font-size: 0.875rem

		i
			width: 1.25rem
			height: 1.25rem

		&--success
			border: 1px solid rgba(var(--lg-security-tip-green-rgb), 0.4)
			background-color: var(--lg-security-tip-background)
			box-shadow: var(--shadow-default)
			color: var(--lg-dark-gray-200)

			i
				color: var(--lg-security-tip-green)

		&--error
			background-color: rgba(var(--lg-red-active-rgb), 0.1)
			color: var(--lg-red-active)

	&__empty
		margin: 0
		font-family: var(--lgei-text)
		font-size: 1rem
		color: var(--club--text-default)

	&__grid
		display: grid
		grid-template-columns: minmax(0, 1fr)
		gap: 1rem

		+respond-to($breakpoint-lg-min)
			grid-template-columns: repeat(2, minmax(0, 1fr))

		+respond-to($breakpoint-xs-min)
			grid-template-columns: repeat(4, minmax(0, 1fr))

	&__card
		position: relative
		display: flex
		align-items: center
		gap: 1rem
		min-width: 0
		min-height: 8.25rem
		padding: 1rem
		overflow: hidden
		border: 1px solid var(--lg-light-gray-300)
		border-radius: 1rem
		background-color: var(--lg-light-gray-100)

	&__image
		display: grid
		place-items: center
		flex: 0 0 6.25rem
		width: 6.25rem
		height: 6.25rem
		padding: 1.125rem
		border-radius: 0.5rem
		background-color: var(--white)

		img
			display: block
			width: 4rem
			height: 4rem
			object-fit: contain

	&__content
		display: flex
		flex-direction: column
		align-items: flex-start
		min-width: 0

		h2, p
			max-width: 100%
			margin: 0
			overflow: hidden
			text-overflow: ellipsis
			white-space: nowrap

		h2
			margin-top: 0.75rem
			font-family: var(--lgei-headline)
			font-size: 1.25rem
			font-weight: 400
			line-height: 1
			color: var(--lg-dark-gray-200)

		p
			margin-top: 0.5rem
			font-family: var(--lgei-text)
			font-size: 1rem
			line-height: 1
			color: var(--lg-mid-gray-200)

	&__category
		max-width: 100%
		padding: 0.25rem 0.5rem
		overflow: hidden
		border-radius: 0.25rem
		font-family: var(--lgei-headline)
		font-size: 0.625rem
		font-weight: 600
		line-height: 1
		text-overflow: ellipsis
		text-transform: uppercase
		white-space: nowrap
		background-color: var(--lg-mid-gray-100)
		color: var(--lg-dark-gray-300)

	&__delete
		position: absolute
		top: 0.6875rem
		right: 0.6875rem

	@media (max-width: 27rem)
		&__card
			gap: 0.75rem

		&__image
			flex-basis: 5rem
			width: 5rem
			height: 5rem
			padding: 0.75rem

			img
				width: 3.5rem
				height: 3.5rem

		&__content h2
			font-size: 1.125rem

.profile-feedback-enter-active,
.profile-feedback-leave-active
	transition: opacity 0.2s ease, transform 0.2s ease

.profile-feedback-enter-from,
.profile-feedback-leave-to
	opacity: 0
	transform: translateY(0.25rem)

@media (prefers-reduced-motion: reduce)
	.profile-feedback-enter-active,
	.profile-feedback-leave-active
		transition: none
</style>
